import { createTheme } from '@mui/material/styles';
import './theme.d';

const theme = createTheme({
  palette: {
    primary: {
      main: '#c2b8a8', // Beige/tan color
      light: '#d4cdc0',
      dark: '#9a9080',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#FDD835', // Yellow from the design
      light: '#FFEB3B',
      dark: '#F57F17',
      contrastText: '#000000'
    },
    background: {
      default: '#fbfbfa',
      paper: '#FAFAFA'
    },
    text: {
      primary: '#2E2E2E',
      secondary: '#666666'
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
      contrastText: '#FFFFFF'
    },
    info: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
      contrastText: '#FFFFFF'
    },
    warning: {
      main: '#FF9800',
      light: '#FFB74D',
      dark: '#F57C00',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#F44336',
      light: '#E57373',
      dark: '#D32F2F',
      contrastText: '#FFFFFF'
    },
    forest: {
      main: '#455b40',
      light: '#5a7654',
      dark: '#34442f',
      contrastText: '#FFFFFF'
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121'
    }
  },
  typography: {
    fontFamily: '"Lora", serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: '#FFFFFF',
      fontFamily: '"Lora", serif'
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      fontFamily: '"Lora", serif'
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
      fontFamily: '"Lora", serif'
    },
    h4: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      fontFamily: '"Lora", serif'
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      fontFamily: '"Lora", serif'
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      fontFamily: '"Lora", serif'
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontFamily: '"Lora", serif'
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontFamily: '"Lora", serif'
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.75,
      fontFamily: '"Lora", serif'
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
      fontFamily: '"Lora", serif'
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.75,
      textTransform: 'none',
      fontFamily: '"Lora", serif'
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
      fontFamily: '"Lora", serif'
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 2.66,
      textTransform: 'uppercase',
      fontFamily: '"Lora", serif'
    }
  },
  shape: {
    borderRadius: 8
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(194,184,168,0.2),0px 4px 5px 0px rgba(194,184,168,0.14),0px 1px 10px 0px rgba(194,184,168,0.12)',
    '0px 3px 5px -1px rgba(194,184,168,0.2),0px 5px 8px 0px rgba(194,184,168,0.14),0px 1px 14px 0px rgba(194,184,168,0.12)',
    '0px 3px 5px -1px rgba(194,184,168,0.2),0px 6px 10px 0px rgba(194,184,168,0.14),0px 1px 18px 0px rgba(194,184,168,0.12)',
    '0px 4px 5px -2px rgba(194,184,168,0.2),0px 7px 10px 1px rgba(194,184,168,0.14),0px 2px 16px 1px rgba(194,184,168,0.12)',
    '0px 5px 5px -3px rgba(194,184,168,0.2),0px 8px 10px 1px rgba(194,184,168,0.14),0px 3px 14px 2px rgba(194,184,168,0.12)',
    '0px 5px 6px -3px rgba(194,184,168,0.2),0px 9px 12px 1px rgba(194,184,168,0.14),0px 3px 16px 2px rgba(194,184,168,0.12)',
    '0px 6px 6px -3px rgba(194,184,168,0.2),0px 10px 14px 1px rgba(194,184,168,0.14),0px 4px 18px 3px rgba(194,184,168,0.12)',
    '0px 6px 7px -4px rgba(194,184,168,0.2),0px 11px 15px 1px rgba(194,184,168,0.14),0px 4px 20px 3px rgba(194,184,168,0.12)',
    '0px 7px 8px -4px rgba(194,184,168,0.2),0px 12px 17px 2px rgba(194,184,168,0.14),0px 5px 22px 4px rgba(194,184,168,0.12)',
    '0px 7px 8px -4px rgba(194,184,168,0.2),0px 13px 19px 2px rgba(194,184,168,0.14),0px 5px 24px 4px rgba(194,184,168,0.12)',
    '0px 7px 9px -4px rgba(194,184,168,0.2),0px 14px 21px 2px rgba(194,184,168,0.14),0px 5px 26px 4px rgba(194,184,168,0.12)',
    '0px 8px 9px -5px rgba(194,184,168,0.2),0px 15px 22px 2px rgba(194,184,168,0.14),0px 6px 28px 5px rgba(194,184,168,0.12)',
    '0px 8px 10px -5px rgba(194,184,168,0.2),0px 16px 24px 2px rgba(194,184,168,0.14),0px 6px 30px 5px rgba(194,184,168,0.12)',
    '0px 8px 11px -5px rgba(194,184,168,0.2),0px 17px 26px 2px rgba(194,184,168,0.14),0px 6px 32px 5px rgba(194,184,168,0.12)',
    '0px 9px 11px -5px rgba(194,184,168,0.2),0px 18px 28px 2px rgba(194,184,168,0.14),0px 7px 34px 6px rgba(194,184,168,0.12)',
    '0px 9px 12px -6px rgba(194,184,168,0.2),0px 19px 29px 2px rgba(194,184,168,0.14),0px 7px 36px 6px rgba(194,184,168,0.12)',
    '0px 10px 13px -6px rgba(194,184,168,0.2),0px 20px 31px 3px rgba(194,184,168,0.14),0px 8px 38px 7px rgba(194,184,168,0.12)',
    '0px 10px 13px -6px rgba(194,184,168,0.2),0px 21px 33px 3px rgba(194,184,168,0.14),0px 8px 40px 7px rgba(194,184,168,0.12)',
    '0px 10px 14px -6px rgba(194,184,168,0.2),0px 22px 35px 3px rgba(194,184,168,0.14),0px 8px 42px 7px rgba(194,184,168,0.12)',
    '0px 11px 14px -7px rgba(194,184,168,0.2),0px 23px 36px 3px rgba(194,184,168,0.14),0px 9px 44px 8px rgba(194,184,168,0.12)',
    '0px 11px 15px -7px rgba(194,184,168,0.2),0px 24px 38px 3px rgba(194,184,168,0.14),0px 9px 46px 8px rgba(194,184,168,0.12)'
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 25,
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: '"Lora", serif'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(194, 184, 168, 0.1)'
        }
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 1200px)': {
            maxWidth: '1200px'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontFamily: '"Lora", serif'
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"Lora", serif'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Lora", serif'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Lora", serif'
        }
      }
    }
  }
});

export default theme;